import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { format, min, sub } from 'date-fns';
import { numberFormat, PointOptionsObject } from 'highcharts';
import { EarningsPerDay } from '../../types';
import { CurrencyInPenniesPipe } from '../currency-in-pennies.pipe';

@Component({
  selector: 'lohi-earnings-chart',
  templateUrl: './earnings-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EarningsChartComponent implements OnInit {
  public clonedEarnings: EarningsPerDay[];
  @Input() public earningsPerDay: EarningsPerDay[];
  @Input() public driverTarget: number;
  @Input() public carrier = false;

  public get format(): string {
    return this.carrier ? DATE_FORMAT : DATETIME_FORMAT;
  }

  public get labels(): Array<string> {
    if (!this.earningsPerDay) {
      return [];
    }
    this.clonedEarnings = [...this.earningsPerDay];
    if (this.clonedEarnings?.length < 10) {
      let minDate = min(this.clonedEarnings.map((value) => new Date(value.date)));
      while (this.clonedEarnings.length < 10) {
        minDate = sub(minDate, { days: 1 });
        this.clonedEarnings.push({
          date: minDate.toISOString(),
          lostEarnings: 0,
          earnings: 0,
          duration: 0,
          loadsLost: 0,
          loadsCompleted: 0,
          endedShiftEarly: false,
          endedShiftEarlyMinutes: 0,
          excessDeadhead: 0,
          excessLineHaul: 0,
          excessMineTime: 0,
          excessWellTime: 0,
          timeToAccept: 0,
          rejectedMinutes: 0,
          flickedMinutes: 0,
        });
      }
    }
    return this.carrier
      ? this.clonedEarnings?.slice().map((value) => {
          return format(new Date(value.date), this.format, {
            useAdditionalDayOfYearTokens: true,
            useAdditionalWeekYearTokens: true,
          });
        })
      : this.clonedEarnings
          ?.slice()
          .reverse()
          .map((value) => {
            return format(new Date(value.date), this.format, {
              useAdditionalDayOfYearTokens: true,
              useAdditionalWeekYearTokens: true,
            });
          });
  }

  public get target(): number {
    return this.driverTarget;
  }

  public chart: Chart;

  constructor(private currencyPipe: CurrencyInPenniesPipe) {}

  public ngOnInit() {
    const initialCount = this.earningsPerDay?.length || 10;
    const initialCountString = `${initialCount} Shift${initialCount !== 1 ? 's' : ''}`;
    const labels = this.labels;
    const title = this.carrier
      ? '<div class="text-lg font-bold">Revenue <span class="text-blue-500">Past 14d</span></div>'
      : `<div class="text-lg font-bold">Driver Earnings <span class="text-blue-500">Past ${initialCountString} </span></div>`;
    this.chart = new Chart({
      chart: {
        type: 'column',
      },
      title: {
        align: 'left',
        useHTML: true,
        text: title,
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          labels: {
            enabled: false,
          },
          tickInterval: 0,
          title: { text: null },
          visible: !!this.driverTarget,
          plotLines: [
            {
              color: '#448AFF',
              dashStyle: 'Dash',
              acrossPanes: true,
              zIndex: 1000,
              label: {
                text: `<span class="text-blue-500">Target ${this.currencyPipe.transform(this.driverTarget)}</span>`,
                useHTML: true,
                align: 'right',
              },
              value: this.driverTarget,
            },
          ],
        },
        {
          labels: {
            enabled: false,
          },
          title: { text: null },
        },
      ],
      plotOptions: {
        series: {},
      },
      xAxis: {
        type: 'category',
        categories: labels,
        visible: true,
      },
      series: [],
    });

    const s: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice().reverse()) {
      s.push({
        label: format(new Date(date.date), this.format, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        }),
        y: date.earnings,
        custom: {
          duration: date.duration,
        },
      });
      /*s.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.earnings as number,
      ]);*/
    }
    const s2: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice().reverse()) {
      s2.push({
        label: format(new Date(date.date), this.format, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        }),
        y: date.lostEarnings,
        custom: {
          duration: date.duration,
        },
      });
      /*s2.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.lostEarnings as number,
      ]);*/
    }
    this.chart.addSeries(
      {
        name: 'Earnings',
        type: 'column',
        tooltip: {
          pointFormatter: formatCurrentTooltip,
        },
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, '#070707'],
            [1, '#3e3e52'],
          ],
        },
        dataLabels: {
          enabled: true,
          formatter: formatCurrency,
          allowOverlap: false,
          borderColor: 'transparent',
        },
        data: s,
        yAxis: 1,
      },
      true,
      true,
    );
    this.chart.addSeries(
      {
        name: 'Lost Earnings',
        type: 'column',
        tooltip: {
          pointFormatter: formatCurrentTooltip,
        },
        color: '#EF6048',
        dataLabels: {
          enabled: true,
          formatter: formatCurrency,
          color: '#EF6048',
          allowOverlap: false,
          borderColor: 'transparent',
        },
        data: s2,
        yAxis: 1,
      },
      true,
      true,
    );
    /*const s3: Array<[number | string, number | null]> = [];
    for (const date of this.earningsPerDay.slice().reverse()) {
      s3.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.loadsCompleted as number,
      ]);
    }
    this.chart.addSeries(
      {
        name: 'Loads Completed',
        type: 'spline',
        color: '#82ff00',
        data: s3,
        yAxis: 1,
      },
      true,
      true,
    );
    const s4: Array<[number | string, number | null]> = [];
    for (const date of this.earningsPerDay.slice().reverse()) {
      s4.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.loadsLost as number,
      ]);
    }
    this.chart.addSeries(
      {
        name: 'Loads Lost',
        type: 'spline',
        color: '#ff0000',
        data: s4,
        yAxis: 1,
      },
      true,
      true,
    );*/
  }
}

const DATETIME_FORMAT = 'M/dd/yyyy hh:mm';
const DATE_FORMAT = 'M/dd/yyyy';

function formatCurrentTooltip() {
  return `${this.series.name}: <b>$${numberFormat(this.y / 100, 2)}</b>
<br>Available Time: <b>${this.custom.duration > 60 ? Math.round(this.custom.duration / 60) + 'h' : ''} ${
    this.custom.duration % 60 > 0 ? Math.round(this.custom.duration % 60) + 'm' : ''
  }</b>`;
}

function formatCurrency() {
  if (this.y === 0) {
    return '';
  }
  return '$' + numberFormat(this.y / 100, 2);
}
